import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios';
import { message } from 'antd';
import { useAuthStore } from '../stores/authStore';

// API响应接口
export interface ApiResponse<T = any> {
  code?: number;
  message: string;
  data: T;
  success: boolean;
}

// 分页响应接口
export interface PagedResponse<T = any> {
  list: T[];
  total: number;
  page: number;
  size: number;
}

// 请求配置接口
export interface RequestConfig extends AxiosRequestConfig {
  showError?: boolean; // 是否显示错误提示
  showLoading?: boolean; // 是否显示加载状态
}

// 创建axios实例
const api: AxiosInstance = axios.create({
  baseURL: (import.meta as any).env?.VITE_API_BASE_URL || 'http://localhost:8080',
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// 标记是否已经处理过401错误，避免重复跳转
let isHandling401 = false;

// 请求拦截器
api.interceptors.request.use(
  (config) => {
    // 从认证store获取token
    const token = useAuthStore.getState().token;
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    // 开发环境请求日志（便于定位 rule_id 传值）
    try {
      if ((import.meta as any).env?.DEV) {
        const url = config.url || '';
        if (url.includes('/api/links/update') || url.includes('/api/rules/update')) {
          // 尝试解析 data 便于阅读
          let payload: any = config.data;
          try { payload = typeof payload === 'string' ? JSON.parse(payload) : payload; } catch {}
          // 仅输出必要字段，避免泄漏敏感头
          // eslint-disable-next-line no-console
          console.log('[API][REQ]', url, { method: config.method, payload });
        }
      }
    } catch {}
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// 响应拦截器
api.interceptors.response.use(
  (response: AxiosResponse<ApiResponse>) => {
    const { data } = response;
    
    // 处理业务错误 - 直接返回响应，让业务层处理
    return response;
  },
  (error) => {
    // 处理网络错误
    if (error.response) {
      const { status, data } = error.response;
      try {
        if ((import.meta as any).env?.DEV) {
          const url = error.config?.url || '';
          if (url.includes('/api/links/update') || url.includes('/api/rules/update')) {
            // eslint-disable-next-line no-console
            console.warn('[API][ERR]', url, { status, data });
          }
        }
      } catch {}
      
      switch (status) {
        case 401:
          // 避免重复处理401错误
          if (!isHandling401) {
            isHandling401 = true;
            
            // 清除认证信息
            useAuthStore.getState().logout();
            
            // 显示错误消息
            message.error('登录已过期，请重新登录');
            
            // 延迟跳转，避免在组件卸载时跳转
            setTimeout(() => {
              // 检查当前是否在登录页面，避免重复跳转
              if (window.location.pathname !== '/login') {
                window.location.href = '/login';
              }
              isHandling401 = false;
            }, 100);
          }
          break;
        case 403: {
          const errorCode403 = (data && (data as any).error_code) as string | undefined;
          const reqUrl403 = error.config?.url || '';
          // 账号被禁用（到期）- 非登录接口才由拦截器处理，登录接口交给 Login 页面自行处理
          if (errorCode403 === 'ACCOUNT_DISABLED' && !reqUrl403.includes('/auth/login')) {
            useAuthStore.getState().logout();
            message.error(data?.message || '账号到期，请联系客服升级');
            setTimeout(() => {
              if (window.location.pathname !== '/login') {
                window.location.href = '/login';
              }
            }, 100);
            break;
          }
          const code403 = (data && (data as any).code) as string | undefined;
          if (code403 && String(code403).startsWith('FREE_PLAN_')) {
            break;
          }
          // 对于403错误，只在非权限加载相关接口时显示错误
          if (!reqUrl403.includes('/auth/login') && !reqUrl403.includes('/auth/menu-permissions') && !reqUrl403.includes('/permissions/user')) {
            message.error(data?.message || '权限不足');
          }
          break;
        }
        case 404:
          // 对于404错误，只在非权限相关接口时显示错误
          const url404 = error.config?.url || '';
          if (!url404.includes('/auth/menu-permissions') && !url404.includes('/permissions/user')) {
            message.error('请求的资源不存在');
          }
          break;
        case 500:
          message.error('服务器内部错误');
          break;
        default:
          message.error(data?.message || '请求失败');
      }
    } else if (error.request) {
      message.error('网络连接失败');
    } else {
      message.error('请求配置错误');
    }
    
    return Promise.reject(error);
  }
);

// 导出API实例
export default api;

// 封装POST请求
export const post = async <T = any>(
  url: string,
  data?: any,
  config?: RequestConfig
): Promise<ApiResponse<T>> => {
  try {
    const response = await api.post(url, data, config);
    return response.data;
  } catch (error) {
    throw error;
  }
};

// 封装GET请求
export const get = async <T = any>(
  url: string,
  config?: RequestConfig
): Promise<T> => {
  try {
    const response = await api.get(url, config);
    return response.data;
  } catch (error) {
    throw error;
  }
}; 